/**
 * WebGPU Wireframe Renderer
 *
 * GPU-accelerated wireframe rendering with Trinity blue depth gradient.
 * Follows the SwarmGPU engine pattern (swarm-gpu.ts): class-based, inline WGSL,
 * shared device, async init with fallback.
 *
 * All rotation, projection, and depth coloring happens on the GPU.
 * Per-frame CPU work: write 80 bytes to uniform buffer.
 */

import type { WireframeMesh } from './geometry-generators';

// ---------------------------------------------------------------------------
// WGSL Shader
// ---------------------------------------------------------------------------

const SHADER_SOURCE = /* wgsl */ `

// Uniform buffer: rotation matrix + projection params + animation (96 bytes / 24 floats)
struct Uniforms {
  rot_row0: vec4f,    // rotation matrix row 0 (xyz used, w pad)
  rot_row1: vec4f,    // rotation matrix row 1
  rot_row2: vec4f,    // rotation matrix row 2
  fov: f32,
  distance: f32,
  half_w: f32,
  half_h: f32,
  z_min: f32,
  z_range: f32,
  animation_progress: f32,  // 0.0 = hidden, 1.0 = fully visible
  edge_count: f32,          // total edges for normalization
}

@group(0) @binding(0) var<storage, read> positions: array<vec4f>;
@group(0) @binding(1) var<storage, read> edges: array<vec2u>;
@group(0) @binding(2) var<uniform> u: Uniforms;

struct VsOut {
  @builtin(position) pos: vec4f,
  @location(0) depth_norm: f32,
  @location(1) @interpolate(flat) edge_progress: f32,
}

@vertex
fn vs_main(
  @builtin(vertex_index) vid: u32,
  @builtin(instance_index) iid: u32,
) -> VsOut {
  let edge = edges[iid];
  let vert_idx = select(edge.x, edge.y, vid == 1u);
  let p = positions[vert_idx].xyz;

  // Apply rotation: rotated = R * p (dot each row with p)
  let rotated = vec3f(
    dot(u.rot_row0.xyz, p),
    dot(u.rot_row1.xyz, p),
    dot(u.rot_row2.xyz, p),
  );

  // Perspective projection
  let z_eye = rotated.z + u.distance;
  let scale = u.fov / z_eye;
  let screen = vec2f(rotated.x * scale, rotated.y * scale);

  // Convert to NDC: [-1, 1]
  let ndc = vec2f(screen.x / u.half_w, -(screen.y / u.half_h));

  // Depth for coloring (0 = far, 1 = near)
  let depth_norm = select(
    (z_eye - u.z_min) / u.z_range,
    0.5,
    u.z_range < 0.001
  );

  // Calculate edge animation progress: edges appear in sequence
  // Edge appearance is staggered based on instance index
  let edge_index_norm = f32(iid) / max(u.edge_count, 1.0);
  // Smoothly interpolate with a delay offset
  let edge_progress = smoothstep(-0.1, 0.9, u.animation_progress - edge_index_norm * 0.5);

  var out: VsOut;
  out.pos = vec4f(ndc, 0.5, 1.0);
  out.depth_norm = clamp(depth_norm, 0.0, 1.0);
  out.edge_progress = edge_progress;
  return out;
}

@fragment
fn fs_main(in: VsOut) -> @location(0) vec4f {
  let d = in.depth_norm;

  // Trinity blue depth gradient — matches Canvas2D exactly:
  // #336699 (far) → #3696E9 (near)
  let r = (51.0 + d * 3.0) / 255.0;
  let g = (102.0 + d * 48.0) / 255.0;
  let b = (153.0 + d * 80.0) / 255.0;
  let base_a = 0.08 + d * 0.85;

  // Modulate alpha by edge appearance progress for animation effect
  let a = base_a * in.edge_progress;

  // Premultiplied alpha output
  return vec4f(r * a, g * a, b * a, a);
}
`;

// ---------------------------------------------------------------------------
// Shared Device Singleton (refcounted)
// ---------------------------------------------------------------------------

let sharedDevice: GPUDevice | null = null;
let deviceRefCount = 0;
let pendingDevice: Promise<GPUDevice> | null = null;

async function acquireSharedDevice(): Promise<GPUDevice> {
  if (sharedDevice && deviceRefCount > 0) {
    deviceRefCount++;
    return sharedDevice;
  }
  // Serialize: if another init is in flight, wait for it instead of creating a second device
  if (pendingDevice) {
    const d = await pendingDevice;
    deviceRefCount++;
    return d;
  }
  pendingDevice = (async () => {
    const adapter = await navigator.gpu?.requestAdapter();
    if (!adapter) throw new Error('WebGPU not supported');
    const device = await adapter.requestDevice();
    device.lost.then(() => {
      sharedDevice = null;
      deviceRefCount = 0;
      pendingDevice = null;
      pipelineCache.clear();
    });
    sharedDevice = device;
    deviceRefCount = 1;
    pendingDevice = null;
    return device;
  })();
  return pendingDevice;
}

function releaseSharedDevice(): void {
  deviceRefCount--;
  if (deviceRefCount <= 0 && sharedDevice) {
    sharedDevice.destroy();
    sharedDevice = null;
    deviceRefCount = 0;
    pipelineCache.clear();
  }
}

// ---------------------------------------------------------------------------
// Shared Pipeline Cache (one per format)
// ---------------------------------------------------------------------------

const pipelineCache = new Map<string, { pipeline: GPURenderPipeline; layout: GPUBindGroupLayout }>();

function getSharedPipeline(device: GPUDevice, format: GPUTextureFormat) {
  const key = format;
  const cached = pipelineCache.get(key);
  if (cached) return cached;

  const module = device.createShaderModule({ code: SHADER_SOURCE });

  const bindGroupLayout = device.createBindGroupLayout({
    entries: [
      { binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
      { binding: 1, visibility: GPUShaderStage.VERTEX, buffer: { type: 'read-only-storage' } },
      { binding: 2, visibility: GPUShaderStage.VERTEX, buffer: { type: 'uniform' } },
    ],
  });

  const pipeline = device.createRenderPipeline({
    layout: device.createPipelineLayout({ bindGroupLayouts: [bindGroupLayout] }),
    vertex: { module, entryPoint: 'vs_main' },
    fragment: {
      module,
      entryPoint: 'fs_main',
      targets: [{
        format,
        blend: {
          // Additive blending for glow accumulation at edge intersections
          color: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
          alpha: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
        },
      }],
    },
    primitive: { topology: 'line-list' },
  });

  const entry = { pipeline, layout: bindGroupLayout };
  pipelineCache.set(key, entry);
  return entry;
}

// ---------------------------------------------------------------------------
// rAF Coordinator (single loop for all instances)
// ---------------------------------------------------------------------------

export type TickCallback = () => void;

const tickCallbacks = new Set<TickCallback>();
let rafId = 0;

function rafLoop() {
  for (const cb of tickCallbacks) cb();
  if (tickCallbacks.size > 0) {
    rafId = requestAnimationFrame(rafLoop);
  }
}

export function registerTick(callback: TickCallback): () => void {
  const wasEmpty = tickCallbacks.size === 0;
  tickCallbacks.add(callback);
  if (wasEmpty) {
    rafId = requestAnimationFrame(rafLoop);
  }
  return () => {
    tickCallbacks.delete(callback);
    if (tickCallbacks.size === 0) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
  };
}

// ---------------------------------------------------------------------------
// WireframeGPU Class
// ---------------------------------------------------------------------------

const UNIFORM_SIZE = 96; // 24 × f32

export interface WireframeGPUOptions {
  mesh: WireframeMesh;
  fov?: number;
  distance?: number;
  backgroundColor?: string;
  animationDuration?: number; // Duration in ms, set to 0 to disable animation
}

export class WireframeGPU {
  private canvas: HTMLCanvasElement;
  private mesh: WireframeMesh;
  private fov: number;
  private distance: number;

  private device!: GPUDevice;
  private context!: GPUCanvasContext;
  private format!: GPUTextureFormat;

  private positionBuffer!: GPUBuffer;
  private edgeBuffer!: GPUBuffer;
  private uniformBuffer!: GPUBuffer;
  private bindGroup!: GPUBindGroup;
  private pipeline!: GPURenderPipeline;

  private edgeCount: number;
  private rx = 0;
  private ry = 0;
  private rz = 0;

  // Bounding sphere for depth range
  private zMin: number;
  private zRange: number;

  // Animation state
  private animationDuration: number; // ms, 0 = disabled
  private animationStartTime: number = 0;
  private isAnimating: boolean = false;

  private transparent: boolean;
  private destroyed = false;
  private deviceAcquired = false;

  constructor(canvas: HTMLCanvasElement, opts: WireframeGPUOptions) {
    this.canvas = canvas;
    this.mesh = opts.mesh;
    this.fov = opts.fov ?? 400;
    this.distance = opts.distance ?? 5;
    this.transparent = opts.backgroundColor === 'transparent';
    this.edgeCount = opts.mesh.edges.length;
    this.animationDuration = opts.animationDuration ?? 1200; // Default 1.2 seconds

    // Compute bounding sphere radius
    let maxR = 0;
    for (const v of this.mesh.vertices) {
      const r = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
      if (r > maxR) maxR = r;
    }
    // z_eye ranges from (distance - maxR) to (distance + maxR)
    this.zMin = this.distance - maxR;
    this.zRange = 2 * maxR;
  }

  async init(): Promise<void> {
    this.device = await acquireSharedDevice();
    this.deviceAcquired = true;

    this.context = this.canvas.getContext('webgpu') as GPUCanvasContext;
    this.format = navigator.gpu.getPreferredCanvasFormat();
    this.context.configure({
      device: this.device,
      format: this.format,
      alphaMode: 'premultiplied',
    });

    // Upload position buffer: vec4f per vertex (xyz + pad)
    const vertCount = this.mesh.vertices.length;
    const posData = new Float32Array(vertCount * 4);
    for (let i = 0; i < vertCount; i++) {
      posData[i * 4 + 0] = this.mesh.vertices[i][0];
      posData[i * 4 + 1] = this.mesh.vertices[i][1];
      posData[i * 4 + 2] = this.mesh.vertices[i][2];
      posData[i * 4 + 3] = 0;
    }
    this.positionBuffer = this.device.createBuffer({
      size: posData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.positionBuffer, 0, posData);

    // Upload edge buffer: vec2u per edge
    const edgeData = new Uint32Array(this.edgeCount * 2);
    for (let i = 0; i < this.edgeCount; i++) {
      edgeData[i * 2 + 0] = this.mesh.edges[i][0];
      edgeData[i * 2 + 1] = this.mesh.edges[i][1];
    }
    this.edgeBuffer = this.device.createBuffer({
      size: edgeData.byteLength,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });
    this.device.queue.writeBuffer(this.edgeBuffer, 0, edgeData);

    // Uniform buffer (80 bytes)
    this.uniformBuffer = this.device.createBuffer({
      size: UNIFORM_SIZE,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    // Pipeline + bind group
    const { pipeline, layout } = getSharedPipeline(this.device, this.format);
    this.pipeline = pipeline;

    this.bindGroup = this.device.createBindGroup({
      layout,
      entries: [
        { binding: 0, resource: { buffer: this.positionBuffer } },
        { binding: 1, resource: { buffer: this.edgeBuffer } },
        { binding: 2, resource: { buffer: this.uniformBuffer } },
      ],
    });
  }

  setRotation(rx: number, ry: number, rz: number): void {
    this.rx = rx;
    this.ry = ry;
    this.rz = rz;
  }

  startAnimation(): void {
    if (this.animationDuration > 0 && !this.isAnimating) {
      this.animationStartTime = performance.now();
      this.isAnimating = true;
    }
  }

  resize(w: number, h: number): void {
    this.canvas.width = w;
    this.canvas.height = h;
  }

  tick(): void {
    if (this.destroyed) return;

    // Build rotation matrix: R = Rz * Ry * Rx
    const cx = Math.cos(this.rx), sx = Math.sin(this.rx);
    const cy = Math.cos(this.ry), sy = Math.sin(this.ry);
    const cz = Math.cos(this.rz), sz = Math.sin(this.rz);

    // R = Rz * Ry * Rx — upload row vectors for dot(row, p) in shader
    // Row 0: [cy*cz,             cz*sx*sy - cx*sz,  cx*cz*sy + sx*sz]
    // Row 1: [cy*sz,             cx*cz + sx*sy*sz,  cx*sy*sz - cz*sx]
    // Row 2: [-sy,               cy*sx,              cx*cy           ]

    const data = new Float32Array(24);

    // rot_row0 (vec4f)
    data[0] = cy * cz;
    data[1] = cz * sx * sy - cx * sz;
    data[2] = cx * cz * sy + sx * sz;
    data[3] = 0;

    // rot_row1 (vec4f)
    data[4] = cy * sz;
    data[5] = cx * cz + sx * sy * sz;
    data[6] = cx * sy * sz - cz * sx;
    data[7] = 0;

    // rot_row2 (vec4f)
    data[8] = -sy;
    data[9] = cy * sx;
    data[10] = cx * cy;
    data[11] = 0;

    // Projection params
    data[12] = this.fov;
    data[13] = this.distance;
    data[14] = this.canvas.width / 2;
    data[15] = this.canvas.height / 2;
    data[16] = this.zMin;
    data[17] = this.zRange;

    // Animation progress (0 = hidden, 1+ = fully visible)
    let animationProgress = 1.0; // Default: fully visible (no animation)
    if (this.isAnimating && this.animationDuration > 0) {
      const elapsed = performance.now() - this.animationStartTime;
      animationProgress = elapsed / this.animationDuration;
      if (animationProgress >= 1.0) {
        this.isAnimating = false;
        animationProgress = 1.0;
      }
    }
    data[18] = animationProgress;
    data[19] = this.edgeCount;
    data[20] = 0; // pad
    data[21] = 0; // pad
    data[22] = 0; // pad
    data[23] = 0; // pad

    this.device.queue.writeBuffer(this.uniformBuffer, 0, data);

    // Encode render pass
    const commandEncoder = this.device.createCommandEncoder();
    const textureView = this.context.getCurrentTexture().createView();
    const renderPass = commandEncoder.beginRenderPass({
      colorAttachments: [{
        view: textureView,
        clearValue: this.transparent ? { r: 0, g: 0, b: 0, a: 0 } : { r: 0.0196, g: 0.0196, b: 0.0196, a: 1 },
        loadOp: 'clear',
        storeOp: 'store',
      }],
    });

    renderPass.setPipeline(this.pipeline);
    renderPass.setBindGroup(0, this.bindGroup);
    renderPass.draw(2, this.edgeCount); // 2 verts per line, 1 instance per edge

    renderPass.end();
    this.device.queue.submit([commandEncoder.finish()]);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.positionBuffer?.destroy();
    this.edgeBuffer?.destroy();
    this.uniformBuffer?.destroy();
    if (this.deviceAcquired) {
      releaseSharedDevice();
    }
  }
}
